/**
 * Element Type Selector Dialog
 * Popup for selecting which element type to insert
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Type, Image, Table, User, Layout, QrCode, Hash, FileText, Calendar, Package, MailOpen, Route, CreditCard, Mail, Building, Globe, Receipt, Shield } from 'lucide-react';

interface ElementTypeSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelectType: (type: string) => void;
  onlyContainer?: boolean; // Only show container option
  isHeaderInsert?: boolean; // Inserting into header container
  isFooterInsert?: boolean; // Inserting into footer container
}

const ELEMENT_TYPES = [
  { type: 'container', label: 'Container', icon: Layout, description: 'Raster mit Zeilen und Spalten' },
  { type: 'text', label: 'Text', icon: Type, description: 'Textfeld' },
  { type: 'image', label: 'Bild', icon: Image, description: 'Bild einfügen' },
  { type: 'table', label: 'Tabelle', icon: Table, description: 'Rechnungsposten-Tabelle' },
  { type: 'qrcode', label: 'QR-Code', icon: QrCode, description: 'QR-Code für Überweisung' },
  { type: 'page-number', label: 'Seitenzahl', icon: Hash, description: 'Seitennummerierung' },
  { type: 'small-business-note', label: 'Kleinunternehmer', icon: FileText, description: 'Hinweis zur Kleinunternehmerregelung' },
  { type: 'invoice-number', label: 'Rechnungsnummer', icon: Hash, description: 'Rechnungsnummer' },
  { type: 'invoice-date', label: 'Rechnungsdatum', icon: Calendar, description: 'Datum der Rechnung' },
  { type: 'payment-due-date', label: 'Zahlungsziel', icon: Calendar, description: 'Fälligkeitsdatum' },
  { type: 'order-number', label: 'Bestellnummer', icon: Package, description: 'Bestellnummer/Auftragsnummer' },
  { type: 'leitweg-id', label: 'Leitweg-ID', icon: Route, description: 'Leitweg-ID für E-Rechnung' },
  { type: 'message', label: 'Nachricht', icon: MailOpen, description: 'Nachricht an Kunden' },
  { type: 'sender', label: 'Absender', icon: User, description: 'Absenderadresse' },
  { type: 'recipient', label: 'Empfänger', icon: User, description: 'Empfängeradresse' },
  { type: 'customer-number', label: 'Kundennummer', icon: Hash, description: 'Kundennummer' },
  { type: 'iban', label: 'IBAN', icon: CreditCard, description: 'IBAN-Nummer' },
  { type: 'swift', label: 'SWIFT/BIC', icon: CreditCard, description: 'SWIFT/BIC-Code' },
  { type: 'bank-name', label: 'Bank', icon: Building, description: 'Bankname' },
  { type: 'email', label: 'E-Mail', icon: Mail, description: 'E-Mail-Adresse' },
  { type: 'website', label: 'Website', icon: Globe, description: 'Website-URL' },
  { type: 'court', label: 'Amtsgericht', icon: Building, description: 'Zuständiges Amtsgericht' },
  { type: 'hrn', label: 'HR-Nr', icon: FileText, description: 'Handelsregisternummer' },
  { type: 'country', label: 'Land', icon: Globe, description: 'Land' },
  { type: 'tax-number', label: 'Steuernummer', icon: Receipt, description: 'Steuernummer' },
  { type: 'vat-id', label: 'Ust-IdNr', icon: Shield, description: 'Umsatzsteuer-Identifikationsnummer' },
  { type: 'invoice-info-block', label: 'Rechnungsinfo-Block', icon: Table, description: 'Tabelle mit konfigurierbaren Rechnungsinformationen' },
];

export function ElementTypeSelector({ open, onClose, onSelectType, onlyContainer = false, isHeaderInsert = false, isFooterInsert = false }: ElementTypeSelectorProps) {
  const handleSelect = (type: string) => {
    console.log('Element type selected:', type);
    onSelectType(type);
    onClose();
  };

  // Filter elements based on context
  let availableTypes = ELEMENT_TYPES;
  
  if (onlyContainer) {
    // Only show container for root-level
    availableTypes = ELEMENT_TYPES.filter(t => t.type === 'container');
  } else if (isHeaderInsert) {
    // For header: exclude table, recipient
    availableTypes = ELEMENT_TYPES.filter(t => 
      t.type !== 'table' && 
      t.type !== 'recipient'
    );
  } else if (isFooterInsert) {
    // For footer: exclude table, recipient (same as header)
    availableTypes = ELEMENT_TYPES.filter(t => 
      t.type !== 'table' && 
      t.type !== 'recipient'
    );
  } else {
    // For normal cells: allow all element types including containers
    availableTypes = ELEMENT_TYPES;
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {onlyContainer ? 'Container hinzufügen' : 'Element-Typ auswählen'}
          </DialogTitle>
        </DialogHeader>
        
        {onlyContainer && (
          <p className="text-sm text-muted-foreground mt-2">
            Das erste Element muss ein Container sein. Danach können Sie Elemente in die Container-Zellen einfügen.
          </p>
        )}
        
        <div className="grid grid-cols-2 gap-3 mt-4 overflow-y-auto pr-2">
          {availableTypes.map(({ type, label, icon: Icon, description }) => (
            <button
              key={type}
              onClick={() => handleSelect(type)}
              className="flex items-start gap-3 p-4 rounded-lg border-2 border-border 
                         hover:border-primary hover:bg-accent transition-all text-left
                         group"
            >
              <div className="w-10 h-10 rounded-lg bg-muted group-hover:bg-accent 
                              flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-foreground group-hover:text-primary">
                  {label}
                </div>
                <div className="text-sm text-muted-foreground group-hover:text-primary mt-0.5">
                  {description}
                </div>
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
